/* istanbul ignore file */
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { JsonApiResponse } from '@api/jsonApi'
import seatsAPI, { SeatsAvailabilityApi } from '@api/seats'
import { ApiError } from '@lib/api'
import seatSelectionUtils, { BuildSeatsProps } from '@lib/seatSelection'
import utils from '@lib/utils'
import { useParams } from '@stores/params'

const DEFAULT_SCHEME = { outbound: [], inbound: [] }
const DEFAULT_PRESELECTED_SEATS = { outbound: {}, inbound: {} }

interface UseSeatsQueriesProps {
  connections: { outbound?: Connection; inbound?: Connection } | undefined
  options?: BuildSeatsProps
  enabled?: boolean
}

interface Result extends Seat.Train.Data {
  isLoading: boolean
  error: ApiError | null
  preselectedSeats: Seat.ByTripDirection
}

const useTwoStepSeatsQuery = ({ connections = {}, enabled = true, options }: UseSeatsQueriesProps): Result => {
  const [params] = useParams()

  // TODO: Use correct fare for return trip: https://distribusion.atlassian.net/browse/OWL-3957
  const availabilityParams = useMemo(
    () =>
      Object.entries(connections)
        .filter(([, connection]) => connection)
        .map<SeatsAvailabilityApi.Request>(([tripDirection, connection]) => ({
          ...seatSelectionUtils.buildSeatAvailabilitiesParams(connection, params, options),
          tripDirection: tripDirection as Seat.TripDirection,
        })),
    [connections, options, params],
  )

  const availabilityInfo = useQuery({
    queryKey: ['seats', 'availability', availabilityParams],
    queryFn: async () => {
      const connectionsAvailability = await Promise.all(
        availabilityParams.map(async params => {
          const availability = await seatsAPI.availability(params)
          const jsonApiResponse = new JsonApiResponse(availability)

          const seatsMap = utils.array.mapBy(availability.data, item => {
            return params.tripDirection + item.relationships.element.data.id
          })

          const scheme = {
            [params.tripDirection]: Object.values(jsonApiResponse.groupedData.segments).map<Seat.Train.Segment>(
              segment => {
                const { vehicle, departure_station, arrival_station, departure_time, arrival_time } =
                  jsonApiResponse.parseRelationships(segment)
                const cars = jsonApiResponse.parseRelationships(vehicle).coaches.map(coach => {
                  return {
                    index: coach.attributes.order_position,
                    layout: coach.relationships.layouts.data[0].id,
                    label: coach.attributes.label,
                  }
                })

                return {
                  id: segment.id,
                  departureTime: departure_time,
                  arrivalTime: arrival_time,
                  departureStation: departure_station.attributes,
                  arrivalStation: arrival_station.attributes,
                  index: segment.attributes.index,
                  cars: cars,
                }
              },
            ),
          }

          const preselectedSeats = {
            [params.tripDirection]: Object.values(
              jsonApiResponse?.groupedData.preselected_seat_availabilities ?? [],
            ).reduce<Seat.BySegment>((acc, preselectedData) => {
              const { seat_availability: availability } = jsonApiResponse.parseRelationships(preselectedData)
              const segment = Object.values(jsonApiResponse.groupedData.segments).find(segment =>
                segment.relationships.preselected_seat_availabilities.data.some(({ id }) => id === preselectedData.id),
              )!
              const coach = Object.values(jsonApiResponse.groupedData.coaches).find(coach =>
                coach.relationships.layouts.data.some(({ id }) => id === availability.relationships.layout.data.id),
              )

              const seat: Seat.Selected = {
                code: availability?.attributes.code,
                segmentIndex: segment.attributes.index,
                carIndex: coach?.attributes.order_position,
                fareClass: null,
                price: availability?.attributes.price,
              }

              return {
                ...acc,
                [segment.attributes.index]: [...(acc[segment.attributes.index] ?? []), seat],
              }
            }, {}),
          }

          return { seatsMap, scheme, preselectedSeats }
        }),
      )

      return connectionsAvailability.reduce(
        (acc, { seatsMap, preselectedSeats, scheme }) => ({
          scheme: { ...acc.scheme, ...scheme },
          preselectedSeats: { ...acc.preselectedSeats, ...preselectedSeats },
          seatsMap: new Map([...acc.seatsMap, ...seatsMap]),
        }),
        {
          seatsMap: new Map(),
          preselectedSeats: { outbound: [], inbound: [] } as Seat.ByTripDirection,
          scheme: { outbound: [], inbound: [] } as Seat.ByTripDirection<Seat.Train.Segment[]>,
        },
      )
    },
    enabled: !!availabilityParams.length && enabled,
  })

  const layoutQuery = useCallback(
    (layout: string, direction: Seat.TripDirection, segment: number, car: number) => ({
      queryKey: ['seats', 'layout', 'twoStep', layout, direction, segment, car, params.marketingCarrierCode],
      enabled: !!layout && availabilityInfo.data?.seatsMap && enabled,
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: async (): Promise<Seat.Train.Layout> => {
        const [data] = await seatsAPI.layout({ ids: [layout], marketingCarrierCode: params.marketingCarrierCode })

        return {
          ...data,
          seats: data.elements.map<Seat.Entry>(seat => {
            const { label, item, id, position, size } = seat
            const availability = availabilityInfo.data!.seatsMap.get(direction + id)!

            const orientationMap: Record<Seat.Train.Rotation, Seat.SeatOrientation> = {
              none: 'facingForward',
              half: 'facingBackward',
              three_quarters: 'facingLeft',
              quarter: 'facingRight',
            }

            return {
              id,
              code: item === 'seat' && availability ? availability.attributes.code : item,
              label,
              fareClass: null,
              vacant:
                availability?.attributes.status === 'available' || availability?.attributes.status === 'preselected',
              price: availability?.attributes.price ?? 0,
              orientation: orientationMap[position.transformation.rotation],
              direction: 'straight',
              coordinates: position.anchor_point,
              flipped: position.transformation.horizontal_flip,
              size,
            }
          }),
        }
      },
    }),
    [availabilityInfo.data, enabled, params.marketingCarrierCode],
  )

  return {
    scheme: availabilityInfo.data?.scheme || DEFAULT_SCHEME,
    layoutQuery,
    isLoading: availabilityInfo.isLoading,
    error: availabilityInfo.error as ApiError,
    preselectedSeats: availabilityInfo.data?.preselectedSeats || DEFAULT_PRESELECTED_SEATS,
  }
}

export default useTwoStepSeatsQuery
